import React from 'react';
import { Button } from '@ui/components/button';
import { Icon } from '@ui/assets/icons/IconWrapper';
import { useFilterGroupsActions } from '../hooks';
import type { FilterGroup } from '../types/filter-panel.types';
import type { FilterMetadataResponse } from '../types/filter-metadata.types';
import { FilterInputType } from '../types/filter-metadata.types';
import Styles from './Styles.module.scss';
import classNames from 'classnames';
import { Tag } from 'antd';

interface FilterSummaryDisplayProps {
    filterGroups: FilterGroup[];
    metadata?: FilterMetadataResponse;
    multiSelectInValues?: Record<string, any[]>;
    multiSelectOutValues?: Record<string, any[]>;
}

export const FilterSummaryDisplay: React.FC<FilterSummaryDisplayProps> = ({
    filterGroups,
    metadata,
    multiSelectInValues = {},
    multiSelectOutValues = {},
}) => {
    const { removeCondition } = useFilterGroupsActions();
    const formatValue = (value: any, operator?: string): string => {
        if (value === null || value === undefined) return '';

        if (operator === 'between' && Array.isArray(value) && value.length === 2) {
            return `${value[0]} - ${value[1]}`;
        }

        if (Array.isArray(value)) {
            // Handle objects with value/title properties (for multi-select)
            return value
                .map((v: any) => {
                    if (typeof v === 'object' && v.title !== undefined) {
                        return v.title;
                    }
                    if (typeof v === 'object' && v.value !== undefined) {
                        return String(v.value);
                    }
                    return String(v);
                })
                .join(', ');
        }

        return String(value);
    };

    const formatOperator = (operator?: string): string => {
        if (!operator) return '';
        const operatorMap: Record<string, string> = {
            equals: '=',
            contains: 'contains',
            between: 'between',
            greaterThan: '>',
            lessThan: '<',
            in: 'in',
            exists: 'exists',
            not_exists: 'not exists',
        };
        return operatorMap[operator] || operator;
    };

    const getFieldLabel = (field: string): string => {
        const meta = metadata?.filters?.find(f => f.field === field);
        return meta?.label || field;
    };

    if (!filterGroups || filterGroups.length === 0) {
        return <div className="text-center text-[#8C8C8C]">No filters applied</div>;
    }

    const formatTagValue = (values: any[], operator?: string): React.JSX.Element[] => {
        return values.map((value: any) => {
            return (
                <Tag
                    key={value.value}
                    className={Styles.customFilterTag}
                    closable={false}
                >
                    {value.title || value.value}
                </Tag>
            );
        });
    };

    return (
        <div className="flex flex-col gap-3">
            {filterGroups.map((group, groupIndex) => {
                if (group.conditions.length === 0) return null;

                return (
                    <React.Fragment key={group.id}>
                        {groupIndex > 0 && (
                            <div className="relative flex items-center">
                                <div className="absolute left-0 right-0 h-px bg-[#E8E8E8]"></div>
                                <div className="relative z-10 mx-auto">
                                    <Button
                                        style={
                                            group.operator === 'AND'
                                                ? { backgroundColor: '#DCE0FB', color: '#203B87' }
                                                : { color: '#FF8400', backgroundColor: '#FFE8D0' }
                                        }
                                        className={`${Styles.logicalOperatorButton} ${
                                            group.operator === 'AND' ? Styles.logicalAndButton : Styles.logicalOrButton
                                        }`}
                                    >
                                        {group.operator}
                                    </Button>
                                </div>
                            </div>
                        )}
                        <div className="flex flex-col gap-3 rounded-md bg-[#f5f5f5] p-3">
                            {group.conditions.map((condition, conditionIndex) => {
                                if (!condition.field || !condition.operator) return null;

                                const fieldLabel = getFieldLabel(condition.field);
                                const meta = metadata?.filters?.find(f => f.field === condition.field);
                                const isMultiSelect = meta?.inputType === FilterInputType.MULTI_SELECT;
                                const conditionKey = `${group.id}-${condition.id}`;

                                // Get in/out values for multi-select fields
                                const inValues = isMultiSelect ? multiSelectInValues[conditionKey] || [] : [];
                                const outValues = isMultiSelect ? multiSelectOutValues[conditionKey] || [] : [];

                                const operator = formatOperator(condition.operator);

                                // Check for noteTypeFilters (note_type field)
                                const noteTypeFilters = (condition as any).noteTypeFilters || {};
                                const hasNoteTypeFilters =
                                    condition.field === 'note_type' && Object.keys(noteTypeFilters).length > 0;

                                // Check for measurementValueRanges (measurement_type field)
                                const measurementValueRanges = (condition as any).measurementValueRanges || {};
                                const hasMeasurementRanges =
                                    condition.field === 'measurement_type' &&
                                    Object.keys(measurementValueRanges).length > 0;

                                // Format date range
                                const formatDateRange = (dateRange: any): string => {
                                    if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) return '';
                                    const [start, end] = dateRange;
                                    if (!start || !end) return '';
                                    return `${start} - ${end}`;
                                };

                                return (
                                    <React.Fragment key={condition.id}>
                                        {conditionIndex > 0 && condition.conditionOperator && (
                                            <div className="relative flex items-center">
                                                <div className="absolute left-0 right-0 h-px bg-[#E8E8E8]"></div>
                                                <div className="relative z-10 mx-auto">
                                                    <Button
                                                        style={
                                                            condition.conditionOperator === 'AND'
                                                                ? { backgroundColor: '#DCE0FB', color: '#203B87' }
                                                                : { color: '#FF8400', backgroundColor: '#FFE8D0' }
                                                        }
                                                        className={`${Styles.logicalOperatorButton} ${
                                                            condition.conditionOperator === 'AND'
                                                                ? Styles.logicalAndButton
                                                                : Styles.logicalOrButton
                                                        }`}
                                                    >
                                                        {condition.conditionOperator}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-3">
                                            {isMultiSelect && (inValues.length > 0 || outValues.length > 0) ? (
                                                <>
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="flex flex-1 flex-col gap-3">
                                                            <div className="text-sm text-[#595959]">
                                                                <span className={Styles.fieldLabel}>{fieldLabel}</span>
                                                            </div>
                                                            <div className="flex items-start gap-[20px]">
                                                                {inValues.length > 0 && (
                                                                    <div
                                                                        className={classNames(
                                                                            'flex w-[50%] items-start gap-1 text-sm text-[#595959]',
                                                                            Styles.fieldLabel
                                                                        )}
                                                                    >
                                                                        <span>In</span>{' '}
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {formatTagValue(
                                                                                inValues,
                                                                                condition.operator
                                                                            ).map((tag: any) => tag)}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {outValues.length > 0 && (
                                                                    <div
                                                                        className={classNames(
                                                                            'flex w-[50%] items-start gap-1 text-sm text-[#595959]',
                                                                            Styles.fieldLabel
                                                                        )}
                                                                    >
                                                                        <span>Out</span>{' '}
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {formatTagValue(
                                                                                outValues,
                                                                                condition.operator
                                                                            ).map((tag: any) => tag)}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* Note Type Filters */}
                                                            {hasNoteTypeFilters &&
                                                                Object.entries(noteTypeFilters).map(
                                                                    ([typeName, typeFilter]: [string, any]) => {
                                                                        if (!typeFilter) return null;
                                                                        const parts: string[] = [];
                                                                        if (typeFilter.dateRange) {
                                                                            const dateRangeStr = formatDateRange(
                                                                                typeFilter.dateRange
                                                                            );
                                                                            if (dateRangeStr)
                                                                                parts.push(`Date: ${dateRangeStr}`);
                                                                        }
                                                                        if (typeFilter.countMin !== undefined) {
                                                                            parts.push(`Min: ${typeFilter.countMin}`);
                                                                        }
                                                                        if (typeFilter.countMax !== undefined) {
                                                                            parts.push(`Max: ${typeFilter.countMax}`);
                                                                        }
                                                                        if (parts.length === 0) return null;
                                                                        return (
                                                                            <div
                                                                                key={typeName}
                                                                                className={classNames(
                                                                                    'text-sm text-[#595959]',
                                                                                    Styles.fieldLabel
                                                                                )}
                                                                            >
                                                                                <span className="!text-[#4D5B65]">
                                                                                    {typeName}
                                                                                </span>{' '}
                                                                                <span className="!text-[#101D28]">
                                                                                    {parts.join(', ')}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                            {/* Measurement Value Ranges */}
                                                            {hasMeasurementRanges &&
                                                                Object.entries(measurementValueRanges).map(
                                                                    ([measurementName, range]: [string, any]) => {
                                                                        if (!range) return null;
                                                                        const parts: string[] = [];
                                                                        if (range.min !== undefined) {
                                                                            parts.push(`Min: ${range.min}`);
                                                                        }
                                                                        if (range.max !== undefined) {
                                                                            parts.push(`Max: ${range.max}`);
                                                                        }
                                                                        if (range.outstanding === true) {
                                                                            parts.push('Outstanding');
                                                                        }
                                                                        if (range.normalOnly === true) {
                                                                            parts.push('Normal Only');
                                                                        }
                                                                        if (parts.length === 0) return null;
                                                                        return (
                                                                            <div
                                                                                key={measurementName}
                                                                                className={classNames(
                                                                                    'text-sm text-[#595959]',
                                                                                    Styles.fieldLabel
                                                                                )}
                                                                            >
                                                                                <span className="!text-[#4D5B65]">
                                                                                    {measurementName}
                                                                                </span>{' '}
                                                                                <span className="!text-[#101D28]">
                                                                                    {parts.join(', ')}
                                                                                </span>
                                                                            </div>
                                                                        );
                                                                    }
                                                                )}
                                                        </div>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            className="!h-auto !p-1 hover:!bg-transparent"
                                                            icon={
                                                                <Icon
                                                                    name="CircleMinus"
                                                                    size={16}
                                                                />
                                                            }
                                                            onClick={() => removeCondition(group.id, condition.id)}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex flex-1 flex-col gap-1">
                                                        <div
                                                            className={classNames(
                                                                'text-sm text-[#595959]',
                                                                Styles.fieldLabel
                                                            )}
                                                        >
                                                            <span>{fieldLabel}</span> {operator}
                                                            {formatValue(condition.value, condition.operator) && (
                                                                <span className="!text-[#101D28]">
                                                                    {' '}
                                                                    {formatValue(condition.value, condition.operator)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Note Type Filters */}
                                                        {hasNoteTypeFilters &&
                                                            Object.entries(noteTypeFilters).map(
                                                                ([typeName, typeFilter]: [string, any]) => {
                                                                    if (!typeFilter) return null;
                                                                    const parts: string[] = [];
                                                                    if (typeFilter.dateRange) {
                                                                        const dateRangeStr = formatDateRange(
                                                                            typeFilter.dateRange
                                                                        );
                                                                        if (dateRangeStr)
                                                                            parts.push(`Date: ${dateRangeStr}`);
                                                                    }
                                                                    if (typeFilter.countMin !== undefined) {
                                                                        parts.push(`Min: ${typeFilter.countMin}`);
                                                                    }
                                                                    if (typeFilter.countMax !== undefined) {
                                                                        parts.push(`Max: ${typeFilter.countMax}`);
                                                                    }
                                                                    if (parts.length === 0) return null;
                                                                    return (
                                                                        <div
                                                                            key={typeName}
                                                                            className={classNames(
                                                                                'text-sm text-[#595959]',
                                                                                Styles.fieldLabel
                                                                            )}
                                                                        >
                                                                            <span className="font-medium !text-[#101D28]">
                                                                                {typeName}:
                                                                            </span>{' '}
                                                                            <span className="!text-[#101D28]">
                                                                                {parts.join(', ')}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        {/* Measurement Value Ranges */}
                                                        {hasMeasurementRanges &&
                                                            Object.entries(measurementValueRanges).map(
                                                                ([measurementName, range]: [string, any]) => {
                                                                    if (!range) return null;
                                                                    const parts: string[] = [];
                                                                    if (range.min !== undefined) {
                                                                        parts.push(`Min: ${range.min}`);
                                                                    }
                                                                    if (range.max !== undefined) {
                                                                        parts.push(`Max: ${range.max}`);
                                                                    }
                                                                    if (range.outstanding === true) {
                                                                        parts.push('Outstanding');
                                                                    }
                                                                    if (range.normalOnly === true) {
                                                                        parts.push('Normal Only');
                                                                    }
                                                                    if (parts.length === 0) return null;
                                                                    return (
                                                                        <div
                                                                            key={measurementName}
                                                                            className={classNames(
                                                                                'text-sm text-[#595959]',
                                                                                Styles.fieldLabel
                                                                            )}
                                                                        >
                                                                            <span className="font-medium !text-[#101D28]">
                                                                                {measurementName}:
                                                                            </span>{' '}
                                                                            <span className="!text-[#101D28]">
                                                                                {parts.join(', ')}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                    </div>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        className="!h-auto !p-1 hover:!bg-transparent"
                                                        icon={
                                                            <Icon
                                                                name="CircleMinus"
                                                                size={16}
                                                            />
                                                        }
                                                        onClick={() => removeCondition(group.id, condition.id)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};
